// Mirrors backend MAX_FILE_SIZE_BYTES / MAX_FILE_SIZE_LABEL (config/limits.js).
export const MAX_FILE_SIZE_BYTES = 4 * 1024 * 1024 * 1024; // 4 GB
export const MAX_FILE_SIZE_LABEL = '4 GB';
// Mirrors backend MAX_VIDEO_SECONDS (limits.js): the pipeline ceiling, which only
// the Agency pack unlocks. Every other account is capped at STANDARD_DURATION_SECONDS
// (backend STANDARD_MAX_VIDEO_SECONDS); the per-account cap comes from
// /api/user/usage (maxVideoSeconds) and is what the pre-upload check uses. Balance
// gating stays server-side, where the real ffprobed duration is checked.
export const MAX_DURATION_SECONDS = 15 * 60;  // 15 minutes, Agency pack
export const STANDARD_DURATION_SECONDS = 5 * 60;  // 5 minutes, free / Starter / Growth
export const MAX_DURATION_LABEL = `${Math.round(MAX_DURATION_SECONDS / 60)} minutes`;
export const STANDARD_DURATION_LABEL = `${Math.round(STANDARD_DURATION_SECONDS / 60)} minutes`;

/** Format bytes as "243 MB" or "1.2 GB". */
export function formatFileSize(bytes) {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1000) return `${(mb / 1024).toFixed(1)} GB`;
  return `${Math.round(mb)} MB`;
}

/** Format seconds as "X min Y sec". */
export function formatDuration(totalSeconds) {
  const mins = Math.floor(totalSeconds / 60);
  const secs = Math.round(totalSeconds % 60);
  return `${mins} min ${secs} sec`;
}

/**
 * Read media duration via HTMLMediaElement.
 * Returns duration in seconds, or null when the browser cannot determine it
 * (unsupported container, corrupt file, etc.).
 * The object URL is always revoked when done.
 */
export function getMediaDuration(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const tag = file.type.startsWith('video/') ? 'video' : 'audio';
    const el = document.createElement(tag);

    let settled = false;
    const finish = (val) => {
      if (settled) return;
      settled = true;
      el.src = '';
      URL.revokeObjectURL(url);
      resolve(val);
    };

    // 5-second hard timeout — if the browser can't decode metadata in time, skip the check
    const timer = setTimeout(() => finish(null), 5000);

    el.onloadedmetadata = () => {
      clearTimeout(timer);
      finish(isFinite(el.duration) && el.duration > 0 ? el.duration : null);
    };
    el.onerror = () => {
      clearTimeout(timer);
      finish(null); // Can't read — skip the duration check, allow upload to proceed
    };

    el.preload = 'metadata';
    el.src = url;
  });
}

/**
 * Full pre-upload validation: size first (instant), then duration (async).
 *
 * Returns one of:
 *   { error: string, sizeStr: null, durationStr: null }  — blocked
 *   { error: null,   sizeStr: string, durationStr: string|null } — ok
 *
 * durationStr is null when the browser could not read the duration
 * (the upload is still allowed in that case).
 */
export async function validateMediaFile(file) {
  // 1. Size check — never run the heavier duration check if size already fails
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const sizeGB = (file.size / (1024 * 1024 * 1024)).toFixed(1);
    return {
      error: `File too large. Maximum size is ${MAX_FILE_SIZE_LABEL}. Your file is ${sizeGB} GB.`,
      sizeStr: null,
      durationStr: null,
    };
  }

  const sizeStr = formatFileSize(file.size);

  // 2. Duration check
  const duration = await getMediaDuration(file);
  if (duration !== null && duration > MAX_DURATION_SECONDS) {
    return {
      error: `Video too long. Maximum duration is ${MAX_DURATION_LABEL}. Your video is ${formatDuration(duration)}.`,
      sizeStr: null,
      durationStr: null,
    };
  }

  return {
    error: null,
    sizeStr,
    durationStr: duration !== null ? formatDuration(duration) : null,
  };
}
